import { Component, type ErrorInfo, type ReactNode } from 'react';

import { colour } from './theme';

/**
 * The last line of defence, and the one place in the interface whose job is to lose nothing.
 *
 * ## Why the fallback is not just an apology
 *
 * A crash screen that says "something went wrong, please reload" is the same as no crash
 * screen at all: reloading is exactly what the user was going to do anyway, and the work in
 * front of them is gone either way. The one thing worth doing at this point is getting the
 * drawing out of the wreckage, so the fallback's primary action is **download it**, and the
 * download is produced from a snapshot taken *before* React unmounted anything.
 *
 * Autosave usually covers this. It is scheduled, so the last few seconds of work may not have
 * reached storage — and a crash is precisely the moment those seconds matter.
 *
 * ## What it does not catch
 *
 * React error boundaries catch errors thrown while *rendering*. They do not catch throws
 * inside event handlers, timers, promises, or animation-frame callbacks — which is where the
 * canvas painting lives. That path is guarded separately, in `RenderScheduler`, and the two
 * mechanisms are not interchangeable: this one replaces the tree, that one keeps the loop
 * running. Between them there is no unhandled throw that leaves the editor silently dead.
 */

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  /**
   * Produces the current document as text, for the rescue download.
   *
   * A function rather than a value so nothing is serialised until it is needed — and so the
   * snapshot is taken at the moment of the crash rather than on every render.
   */
  readonly snapshot: () => string;
  /** Somewhere to send the error. Injected so a test can assert it was reported. */
  readonly onError?: (error: unknown, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
  readonly rescued: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, rescued: false };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  private readonly rescue = (): void => {
    /*
     * Written defensively on purpose: this runs after something has already gone wrong, and
     * the failure that broke the tree may well have broken the document too. A rescue button
     * that throws is worse than one that is absent.
     */
    try {
      const text = this.props.snapshot();
      const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'edusketch-recovered.json';
      link.click();
      URL.revokeObjectURL(url);
      this.setState({ rescued: true });
    } catch {
      this.setState({ rescued: false });
    }
  };

  private readonly reload = (): void => {
    globalThis.location.reload();
  };

  override render(): ReactNode {
    const { error, rescued } = this.state;
    if (error === null) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 32,
          background: colour.ink,
          color: colour.line,
          font: '14px/1.6 var(--es-font)',
          zIndex: 1000,
        }}
      >
        <h1 style={{ font: '600 20px/1.3 var(--es-font)', margin: 0 }}>
          EduSketch gặp lỗi và phải dừng lại
        </h1>

        <p style={{ margin: 0, maxWidth: 560, textAlign: 'center', color: '#cbd5f5' }}>
          Bản vẽ của bạn vẫn còn trong bộ nhớ. Hãy tải nó về trước khi tải lại trang — bản lưu tự
          động có thể chưa kịp ghi những thay đổi cuối cùng.
        </p>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            onClick={this.rescue}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: 'none',
              background: colour.accent,
              color: colour.solid,
              font: '600 14px var(--es-font)',
              cursor: 'pointer',
            }}
          >
            Tải bản vẽ về máy
          </button>

          <button
            type="button"
            onClick={this.reload}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: '1px solid #475569',
              background: 'transparent',
              color: colour.line,
              font: '600 14px var(--es-font)',
              cursor: 'pointer',
            }}
          >
            Tải lại trang
          </button>
        </div>

        {rescued && <p style={{ margin: 0, color: '#4ade80' }}>Đã tải xuống bản vẽ.</p>}

        {/*
          The message, not the stack. A stack trace on screen reads as an accusation to a
          teacher who has just lost their lesson; the message is enough for a bug report and
          the full error is in the console for whoever needs it.
        */}
        <code style={{ color: colour.faint, fontSize: 12, maxWidth: 560, textAlign: 'center' }}>
          {error.message}
        </code>
      </div>
    );
  }
}
